import React from "react";
import {
  List,
  SimpleList,
  Datagrid,
  TextField,
  NumberField,
  BooleanField,
  Pagination,
  AutocompleteArrayInput,
  BooleanInput,
} from "react-admin";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { Drawer, useMediaQuery, Box, styled } from "@mui/material";

import ListActions from "./ListActions";
import { exporter } from "./Exporter";
import { useProfile } from "../DataHooks/useProfile";
import PaymentEdit from "./Edit";
import PaymentShow from "./Show";
import { useAvailableCurrencies } from "../useAvailableCurrencies";
import { OFFLINE, BILLPLZ, HITPAY, CREDIT, HITPAY_MY } from "./types";
import PaymentPaidAmount from "./PaymentPaidAmount";
import CustomPickerInput from "../OrderItem/CustomPickerInput";
import CustomDateField from "../Common/CustomDateField";
import { useProfileLocale } from "../Common/locale";

const SHOW_PATH = "/payments/:id/show";
const EDIT_PATH = "/payments/:id";

const paymentTypes = [
  { id: OFFLINE, name: OFFLINE },
  { id: BILLPLZ, name: BILLPLZ },
  { id: HITPAY, name: HITPAY },
  { id: HITPAY_MY, name: "Hitpay MYR" },
  { id: CREDIT, name: CREDIT },
];

const defaultFilters = [
  <AutocompleteArrayInput
    alwaysOn
    label="Type"
    source="type"
    choices={paymentTypes}
  />,
  <BooleanInput label="Paid" source="paid" defaultValue={true} />,
  <CustomPickerInput source="createdAt_gte" label="CreatedAt start" />,
  <CustomPickerInput source="createdAt_lte" label="CreatedAt end" />,
  <CustomPickerInput source="updatedAt_gte" label="UpdatedAt start" />,
  <CustomPickerInput source="updatedAt_lte" label="UpdatedAt end" />,
];

// Styled Components
const RootBox = styled(Box)({
  display: "flex",
});

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    zIndex: 100,
  },
}));

const ListWrapper = styled("div")(({ theme }) => ({
  flexGrow: 1,
  transition: theme.transitions.create(["all"], {
    duration: theme.transitions.duration.enteringScreen,
  }),
}));

const PaymentList = (props) => {
  const { profile } = useProfile();
  const locale = useProfileLocale();
  const location = useLocation();
  const navigate = useNavigate();
  const { isMultiCurrency } = useAvailableCurrencies();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const matchEdit = matchPath({ path: EDIT_PATH }, location.pathname);
  const matchShow = matchPath({ path: SHOW_PATH }, location.pathname);
  const isMatch =
    (matchEdit && matchEdit.params?.id !== "create") ||
    (matchShow && matchShow.params?.id !== "create");

  const handleClose = React.useCallback(() => {
    navigate("/payments");
  }, [navigate]);

  if (!profile) return null;

  return (
    <RootBox>
      <List
        {...props}
        component={ListWrapper}
        actions={<ListActions />}
        exporter={exporter(profile, locale)}
        filter={{ page: profile.page._id }}
        filters={defaultFilters}
        pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100, 200]} />}
        sort={{ field: "createdAt", order: "DESC" }}
      >
        {isSmall ? (
          <SimpleList
            linkType={paymentRowClickMobile}
            primaryText={(record) => <span>{record.order.customerName}</span>}
            secondaryText={(record) => <span>{record.type}</span>}
            tertiaryText={(record) => (
              <span>{record.paid ? "paid" : "unpaid"}</span>
            )}
          />
        ) : (
          <Datagrid
            rowClick={paymentRowClick}
            isRowSelectable={(record) => record?.type === OFFLINE}
          >
            <TextField source="order.customerName" sortable={false} />
            <TextField source="order.shippingName" />
            <TextField source="order.orderNumber" sortable={false} />
            <TextField source="order.status" sortable={false} />
            <TextField source="type" />
            <PaymentPaidAmount source="paidAmount" />
            <TextField source="remarks" />
            <NumberField source="order.invoiceNumber" sortable={false} />
            <BooleanField source="paid" />
            {isMultiCurrency && (
              <TextField source="currencyCode" label="Currency" />
            )}
            <CustomDateField source="createdAt" showTime />
            <CustomDateField source="updatedAt" showTime />
          </Datagrid>
        )}
      </List>

      <StyledDrawer
        variant="persistent"
        open={!!isMatch}
        anchor="right"
        onClose={handleClose}
      >
        {isMatch && matchEdit && (
          <PaymentEdit
            id={matchEdit.params.id}
            onCancel={handleClose}
            {...props}
          />
        )}
        {isMatch && matchShow && (
          <PaymentShow
            id={matchShow.params.id}
            onCancel={handleClose}
            {...props}
          />
        )}
      </StyledDrawer>
    </RootBox>
  );
};

const paymentRowClickMobile = (record) =>
  [BILLPLZ, HITPAY, HITPAY_MY, CREDIT].includes(record.type) ? "show" : "edit";

const paymentRowClick = (id, resource, record) =>
  [BILLPLZ, HITPAY, HITPAY_MY, CREDIT].includes(record.type) ? "show" : "edit";

export default PaymentList;
